import { initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import {
  collection,
  connectFirestoreEmulator,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import {
  connectStorageEmulator,
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes
} from 'firebase/storage';

const firebaseConfig = {
  apiKey: 'AIzaSyAHLfJ1uVGBrECAy_E6EZS4Dxie8eVlsSk',
  authDomain: 'maisonliik.firebaseapp.com',
  projectId: 'maisonliik',
  storageBucket: 'maisonliik.firebasestorage.app',
  messagingSenderId: '390077965339',
  appId: '1:390077965339:web:56cbb9114ff4d9750684be',
  measurementId: 'G-SV5J5RM912'
};

// ----------------------------------------------------------------------------
// Roles and permissions (client-side mirror of firestore.rules — this is a
// UX/visibility convenience only; the rules above are the real boundary).
// Keep PERMISSIONS/ROLE_PERMISSIONS in sync with firestore.rules' isOperator/
// isAdmin/isOwner helpers whenever either side changes.
// ----------------------------------------------------------------------------
export const ROLES = Object.freeze({ OWNER: 'owner', ADMIN: 'admin', OPERATOR: 'operator', VIEWER: 'viewer' });

export const PERMISSIONS = Object.freeze({
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ORDERS: 'view_orders',
  CREATE_ORDER: 'create_order',
  EDIT_ORDER: 'edit_order',
  DELETE_ORDER: 'delete_order',
  MANAGE_PRODUCTS: 'manage_products',
  DELETE_PRODUCT: 'delete_product',
  MANAGE_INVENTORY: 'manage_inventory',
  VIEW_FINANCIALS: 'view_financials',
  MANAGE_WALLET: 'manage_wallet',
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_MEMBERS: 'manage_members',
  USE_CALCULATOR: 'use_calculator',
  WIPE_DATA: 'wipe_data',
  VIEW_ACTIVITY_LOG: 'view_activity_log'
});

const VIEWER_PERMS = [PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_ORDERS, PERMISSIONS.VIEW_FINANCIALS, PERMISSIONS.USE_CALCULATOR];
const OPERATOR_PERMS = [...VIEWER_PERMS, PERMISSIONS.CREATE_ORDER, PERMISSIONS.EDIT_ORDER, PERMISSIONS.MANAGE_PRODUCTS, PERMISSIONS.MANAGE_INVENTORY, PERMISSIONS.MANAGE_WALLET];
// VIEW_ACTIVITY_LOG lands on ADMIN_PERMS (not OWNER_PERMS) specifically to
// mirror firestore.rules' `/activity/{eventId}`'s `allow read: if
// isAdmin(shopId)` exactly — isAdmin() there means "admin or owner"
// (hasRole(['owner','admin'])), same hierarchy as everywhere else in this
// file, not "admin only". Granting it only to OWNER_PERMS would have hidden
// the viewer from an admin who firestore.rules would happily let read it.
const ADMIN_PERMS = [...OPERATOR_PERMS, PERMISSIONS.DELETE_ORDER, PERMISSIONS.DELETE_PRODUCT, PERMISSIONS.MANAGE_SETTINGS, PERMISSIONS.WIPE_DATA, PERMISSIONS.VIEW_ACTIVITY_LOG];
const OWNER_PERMS = [...ADMIN_PERMS, PERMISSIONS.MANAGE_MEMBERS];

const ROLE_PERMISSIONS = Object.freeze({
  [ROLES.VIEWER]: VIEWER_PERMS,
  [ROLES.OPERATOR]: OPERATOR_PERMS,
  [ROLES.ADMIN]: ADMIN_PERMS,
  [ROLES.OWNER]: OWNER_PERMS
});

const SHOP_ID = 'maison-liik';
const isLocal = ['localhost', '127.0.0.1'].includes(location.hostname);
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

if (isLocal) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

const shopRef = doc(db, 'shops', SHOP_ID);
const ordersRef = collection(shopRef, 'orders');
const productsRef = collection(shopRef, 'products');
const settingsRef = doc(shopRef, 'settings', 'main');
const calculationsRef = collection(shopRef, 'pricingCalculations');
const membersRef = collection(shopRef, 'members');
const activityRef = collection(shopRef, 'activity');
// Phase 3 migration (post-Phase-2 hardening, Track 3 — REFACTOR_SECURITY_REPORT.md
// §22): walletTransactions/inventoryHistory used to be unbounded arrays
// embedded in settingsRef's document, risking Firestore's 1 MiB per-document
// limit and rewriting the entire settings document on every single
// transaction/movement. Now their own subcollections, synced the same way
// orders/products already are (see replaceCollection() below) rather than
// bundled into the settings document write.
const walletTransactionsRef = collection(shopRef, 'walletTransactions');
const inventoryHistoryRef = collection(shopRef, 'inventoryHistory');
const gate = document.getElementById('authGate');
const form = document.getElementById('authForm');
const emailInput = document.getElementById('authEmail');
const passwordInput = document.getElementById('authPassword');
const errorBox = document.getElementById('authError');
const submitButton = document.getElementById('authSubmit');
const syncBadge = document.getElementById('firebaseSyncBadge');


let started = false;
let applyingRemote = false;
let pendingState = null;
let syncLoop = null;
let remoteOrders = [];
let remoteProducts = [];
let remoteSettings = null;
let remoteWalletTransactions = [];
let remoteInventoryHistory = [];
let remoteReady = { orders: false, products: false, settings: false, walletTransactions: false, inventoryHistory: false };
let unsubscribers = [];

function clean(value) {
  return JSON.parse(JSON.stringify(value));
}

function setBadge(label, mode = '') {
  if (!syncBadge) return;
  syncBadge.textContent = label;
  syncBadge.className = `firebase-sync ${mode}`;
}

function setAuthError(message = '') {
  errorBox.textContent = message;
  errorBox.classList.toggle('hidden', !message);
}

function authMessage(error) {
  const code = error?.code || '';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'E-mail ou mot de passe incorrect.';
  }
  if (code.includes('too-many-requests')) return 'Trop de tentatives. Réessayez dans quelques minutes.';
  return isLocal
    ? 'Connexion impossible. Vérifiez que les émulateurs Firebase sont démarrés.'
    : 'Connexion impossible. Réessayez.';
}

function showLogin(message) {
  // Must also drop 'auth-pending' (not just 'authenticated') — otherwise it
  // never gets removed for a genuinely signed-out visit (it was only ever
  // cleared in showApp()), which combined with the .auth-pending .auth-panel
  // {display:none} CSS rule (added to fix the login-form flash on refresh
  // for already-authenticated users) would leave BOTH classes on <body> at
  // once and permanently hide the login form — nobody could ever sign in.
  document.body.classList.remove('authenticated', 'auth-pending');
  document.body.classList.add('signed-out');
  gate?.setAttribute('aria-hidden', 'false');
  setBadge('Hors ligne', 'error');
  if (message) setAuthError(message);
  if (passwordInput) passwordInput.value = '';
}

function showApp(user) {
  document.body.classList.remove('auth-pending', 'signed-out');
  document.body.classList.add('authenticated');
  gate?.setAttribute('aria-hidden', 'true');
  const identity = document.getElementById('firebaseIdentity');
  if (identity) identity.textContent = user.email || 'Maison Liik';
}

// Phase 3 migration (Track 3 — REFACTOR_SECURITY_REPORT.md §22): copies
// settings/main's old embedded walletTransactions/inventoryHistory arrays
// into their own subcollections, one document per entry keyed by the
// entry's own stable id (so re-running this — e.g. after a failure
// partway through, or a second admin's tab racing this one — only
// re-writes identical data, never duplicates). The legacy fields are only
// cleared from settings/main *after* every entry has been safely copied —
// if this throws partway through the chunk loop, settings/main still has
// the complete original arrays, so the very next login (readState() below)
// sees the same non-empty legacy data and retries from scratch.
async function migrateEmbeddedArrays(legacyWalletTx, legacyInvHist) {
  if (!auth.currentUser || !hasPermission(PERMISSIONS.MANAGE_SETTINGS)) return; // admin+ only — same gate this file's other schema migration already uses
  const ops = [
    ...legacyWalletTx.map(tx => ({ ref: doc(walletTransactionsRef, String(tx.id)), data: clean(tx) })),
    ...legacyInvHist.map(mv => ({ ref: doc(inventoryHistoryRef, String(mv.id)), data: clean(mv) }))
  ];
  // Firestore batches cap at 500 operations — chunk defensively; this
  // codebase's existing scale is nowhere near that, but a migration should
  // not silently truncate a shop that happens to have grown past it.
  for (let i = 0; i < ops.length; i += 400) {
    const batch = writeBatch(db);
    ops.slice(i, i + 400).forEach(op => batch.set(op.ref, op.data));
    await batch.commit();
  }
  await updateDoc(settingsRef, { walletTransactions: deleteField(), inventoryHistory: deleteField() });
  await logActivity('phase3_migration', 'settings', 'main', { walletTransactions: legacyWalletTx.length, inventoryHistory: legacyInvHist.length });
}

async function readState() {
  const [orderSnap, productSnap, settingsSnap, walletTxSnap, invHistSnap] = await Promise.all([
    getDocs(ordersRef),
    getDocs(productsRef),
    getDoc(settingsRef),
    getDocs(walletTransactionsRef),
    getDocs(inventoryHistoryRef)
  ]);
  const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
  // Whether this shop still has the pre-migration embedded shape is decided
  // by settings/main alone (not "is the subcollection empty", which could
  // also just mean a brand-new shop with genuinely zero transactions yet) —
  // see migrateEmbeddedArrays()'s comment for why this is the safe,
  // retry-idempotent source of truth.
  const legacyWalletTx = Array.isArray(settingsData?.walletTransactions) ? settingsData.walletTransactions : [];
  const legacyInvHist = Array.isArray(settingsData?.inventoryHistory) ? settingsData.inventoryHistory : [];
  const needsMigration = legacyWalletTx.length > 0 || legacyInvHist.length > 0;
  if (needsMigration) migrateEmbeddedArrays(legacyWalletTx, legacyInvHist).catch(error => console.error('Phase 3 migration failed', error));
  return {
    orders: orderSnap.docs.map(item => item.data()),
    products: productSnap.docs.map(item => item.data()),
    settings: settingsData ? {
      ...settingsData,
      walletTransactions: needsMigration ? legacyWalletTx : walletTxSnap.docs.map(item => item.data()),
      inventoryHistory: needsMigration ? legacyInvHist : invHistSnap.docs.map(item => item.data())
    } : settingsData
  };
}

function applyRemoteState() {
  if (!started || applyingRemote || !window.ML_CRM || !Object.values(remoteReady).every(Boolean)) return;
  applyingRemote = true;
  try {
    window.ML_CRM.importState({
      orders: remoteOrders,
      products: remoteProducts,
      settings: remoteSettings ? { ...remoteSettings, walletTransactions: remoteWalletTransactions, inventoryHistory: remoteInventoryHistory } : remoteSettings
    });
  } finally {
    applyingRemote = false;
  }
}

function startListeners() {
  unsubscribers.forEach(unsubscribe => unsubscribe());
  remoteReady = { orders: false, products: false, settings: false, walletTransactions: false, inventoryHistory: false };
  unsubscribers = [
    onSnapshot(ordersRef, snapshot => {
      remoteOrders = snapshot.docs.map(item => item.data());
      remoteReady.orders = true;
      applyRemoteState();
      setBadge('Synchronisé', 'ok');
    }, () => setBadge('Erreur commandes', 'error')),
    onSnapshot(productsRef, snapshot => {
      remoteProducts = snapshot.docs.map(item => item.data());
      remoteReady.products = true;
      applyRemoteState();
      setBadge('Synchronisé', 'ok');
    }, () => setBadge('Erreur produits', 'error')),
    onSnapshot(settingsRef, snapshot => {
      remoteSettings = snapshot.exists() ? snapshot.data() : remoteSettings;
      remoteReady.settings = true;
      applyRemoteState();
      setBadge('Synchronisé', 'ok');
    }, () => setBadge('Erreur paramètres', 'error')),
    onSnapshot(walletTransactionsRef, snapshot => {
      remoteWalletTransactions = snapshot.docs.map(item => item.data());
      remoteReady.walletTransactions = true;
      applyRemoteState();
      setBadge('Synchronisé', 'ok');
    }, () => setBadge('Erreur portefeuille', 'error')),
    onSnapshot(inventoryHistoryRef, snapshot => {
      remoteInventoryHistory = snapshot.docs.map(item => item.data());
      remoteReady.inventoryHistory = true;
      applyRemoteState();
      setBadge('Synchronisé', 'ok');
    }, () => setBadge('Erreur stock', 'error'))
  ];
}

async function replaceCollection(ref, records) {
  const existing = await getDocs(ref);
  const ids = new Set(records.map(item => String(item.id)));
  const batch = writeBatch(db);
  existing.docs.forEach(item => {
    if (!ids.has(item.id)) batch.delete(item.ref);
  });
  records.forEach(item => batch.set(doc(ref, String(item.id)), clean(item)));
  await batch.commit();
}

// inventoryHistory needed a different sync strategy than replaceCollection()
// above — found by the Phase 3 integration test actually running against
// the real emulator + real rules, not just reasoned through on paper.
// firestore.rules deliberately makes /inventoryHistory append-only (`allow
// update, delete: if false`, REFACTOR_SECURITY_REPORT.md §22) since nothing
// in this codebase ever updates or removes a movement record — but
// replaceCollection() unconditionally `.set()`s every record in the local
// array on *every* flush, and Firestore's rules engine evaluates a `.set()`
// on an already-existing document as an 'update', not a 'create' — so the
// second flush cycle after any entry synced once would get rejected outright
// by the very rule meant to protect it. This only ever creates documents
// that don't exist remotely yet, and — correctly, for an audit trail — never
// deletes one just because it's no longer present in the local array either.
async function appendOnlyCollection(ref, records) {
  const existing = await getDocs(ref);
  const existingIds = new Set(existing.docs.map(item => item.id));
  const newRecords = records.filter(item => !existingIds.has(String(item.id)));
  if (!newRecords.length) return;
  const batch = writeBatch(db);
  newRecords.forEach(item => batch.set(doc(ref, String(item.id)), clean(item)));
  await batch.commit();
}

async function flushState() {
  while (pendingState) {
    const state = pendingState;
    pendingState = null;
    setBadge('Enregistrement…');
    const settings = state.settings || {};
    await Promise.all([
      replaceCollection(ordersRef, state.orders || []),
      replaceCollection(productsRef, state.products || []),
      replaceCollection(walletTransactionsRef, settings.walletTransactions || []),
      appendOnlyCollection(inventoryHistoryRef, settings.inventoryHistory || [])
    ]);
    // walletTransactions/inventoryHistory are synced above, in their own
    // subcollections now (Phase 3 — REFACTOR_SECURITY_REPORT.md §22) —
    // stripped out here so a stale embedded copy doesn't get written back
    // into settings/main and reintroduce the size/write-amplification
    // problem this migration exists to fix.
    const { walletTransactions, inventoryHistory, ...settingsToWrite } = settings;
    await writeBatch(db).set(settingsRef, clean(settingsToWrite)).commit();
    setBadge('Synchronisé', 'ok');
  }
}

async function syncState(state) {
  if (!auth.currentUser || applyingRemote) return;
  pendingState = clean(state);
  if (!syncLoop) {
    syncLoop = flushState()
      .catch(error => {
        console.error('Firebase sync failed', error);
        setBadge('Non synchronisé', 'error');
        // The sync badge alone is easy to miss; also raise a toast so a failed
        // background sync doesn't go unnoticed while local changes keep piling up.
        window.toast?.('Synchronisation Firebase impossible. Vos modifications restent enregistrées sur cet appareil.', 'alert');
      })
      .finally(() => { syncLoop = null; });
  }
  return syncLoop;
}

async function loadPricingHistory() {
  if (!auth.currentUser) return [];
  const snapshot = await getDocs(calculationsRef);
  return snapshot.docs
    .map(item => item.data())
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

async function savePricingHistory(items) {
  if (!auth.currentUser) return;
  setBadge('Enregistrement…');
  await replaceCollection(calculationsRef, clean(items || []).map((item, index) => ({
    ...item,
    id: String(item.id || `calculation-${Date.now()}-${index}`)
  })));
  setBadge('Synchronisé', 'ok');
}

async function uploadProductImage(productId, file) {
  if (!auth.currentUser) throw new Error('Connectez-vous avant d’ajouter une photo.');
  if (!file?.type?.startsWith('image/')) throw new Error('Le fichier choisi n’est pas une image.');
  if (file.size > 5 * 1024 * 1024) throw new Error('La photo dépasse 5 Mo.');
  const safeName = String(file.name || 'produit.jpg').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '-');
  const imagePath = `shops/${SHOP_ID}/products/${productId}/${Date.now()}-${safeName}`;
  const target = storageRef(storage, imagePath);
  await uploadBytes(target, file, { contentType: file.type, cacheControl: 'public,max-age=3600' });
  return { imagePath, imageUrl: await getDownloadURL(target) };
}

async function deleteProductImage(imagePath) {
  if (!imagePath || !auth.currentUser) return;
  try {
    await deleteObject(storageRef(storage, imagePath));
  } catch (error) {
    if (error?.code !== 'storage/object-not-found') throw error;
  }
}

// Local mirror of the app's data — cleared on explicit sign-out (and on any
// access-denial path) so a previous session's orders/customers/settings don't
// linger in this browser profile.
const LOCAL_MIRROR_KEYS = ['crm_orders', 'crm_products', 'crm_settings', 'crm_backup_auto', 'crm_data_revision'];
function clearLocalMirror() {
  LOCAL_MIRROR_KEYS.forEach(key => {
    try { localStorage.removeItem(key); } catch (error) { /* ignore */ }
  });
  window.ML_CRM?.clearState?.();
}

function hasPermission(permission) {
  const role = window.ML_FIREBASE.role;
  if (!role) return false;
  return (ROLE_PERMISSIONS[role] || []).includes(permission);
}

// Append-only audit trail for security- and business-sensitive actions.
// Enforced server-side: a member may only write an entry attributed to
// themselves (firestore.rules), and nobody can update/delete past entries.
async function logActivity(action, resourceType, resourceId, details = {}) {
  if (!auth.currentUser) return;
  try {
    await setDoc(doc(activityRef), {
      action,
      resourceType,
      resourceId: String(resourceId ?? ''),
      actorUid: auth.currentUser.uid,
      actorEmail: auth.currentUser.email || '',
      actorRole: window.ML_FIREBASE.role || '',
      at: serverTimestamp(),
      details: clean(details || {})
    });
  } catch (error) {
    // Audit logging must never block the primary action it's describing.
    console.error('Audit log write failed', error);
  }
}

// Membership management (owner/admin only — enforced by firestore.rules, not
// just this check). Requires knowing the target's Firebase Auth UID, which an
// owner/admin obtains from the Firebase console when creating the account —
// there is deliberately no client-side email→UID lookup (Admin-SDK-only).
async function listMembers() {
  if (!auth.currentUser) return [];
  const snapshot = await getDocs(membersRef);
  return snapshot.docs.map(item => ({ uid: item.id, ...item.data() }));
}

async function addMember({ uid, email, displayName, role }) {
  if (!auth.currentUser) throw new Error('Connectez-vous pour gérer les membres.');
  await setDoc(doc(membersRef, uid), {
    email: String(email || '').trim(),
    displayName: String(displayName || email || '').trim(),
    role,
    active: true,
    createdAt: new Date().toISOString(),
    createdBy: auth.currentUser.uid
  });
  await logActivity('member_created', 'member', uid, { email, role });
}

async function updateMember(uid, changes) {
  if (!auth.currentUser) throw new Error('Connectez-vous pour gérer les membres.');
  await updateDoc(doc(membersRef, uid), {
    ...changes,
    updatedAt: new Date().toISOString(),
    updatedBy: auth.currentUser.uid
  });
  await logActivity('member_updated', 'member', uid, changes);
}

// Read-only viewer for the append-only audit trail logActivity() writes to
// (Track 4b, post-Phase-2 hardening — REFACTOR_SECURITY_REPORT.md §21).
// firestore.rules already restricts /activity reads to admin+owner
// (isAdmin(shopId)); this only decides how many entries to fetch.
async function listActivity(max = 100) {
  if (!auth.currentUser) return [];
  const snapshot = await getDocs(query(activityRef, orderBy('at', 'desc'), limit(max)));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
}

async function resetPassword(email) {
  // Neutral outcome regardless of whether the address exists — the caller
  // should always show the same confirmation message either way, so this
  // never confirms or denies which emails have accounts.
  try {
    await sendPasswordResetEmail(auth, String(email || '').trim());
  } catch (error) {
    // Swallow enumeration-revealing errors (user-not-found); surface only
    // genuinely actionable ones (malformed address, network failure).
    if (error?.code !== 'auth/user-not-found') throw error;
  }
}

window.ML_FIREBASE = {
  ready: false,
  isLocal,
  isAdmin: false, // retained for the transition; prefer hasPermission()/role
  role: null,
  PERMISSIONS,
  ROLES,
  hasPermission,
  syncState,
  loadPricingHistory,
  savePricingHistory,
  uploadProductImage,
  deleteProductImage,
  listMembers,
  addMember,
  updateMember,
  resetPassword,
  logActivity,
  listActivity,
  signOut: () => signOut(auth)
};

form?.addEventListener('submit', async event => {
  event.preventDefault();
  setAuthError();
  if (submitButton.disabled) return; // guards against double-submit (e.g. double-click/double-Enter)
  submitButton.disabled = true;
  submitButton.textContent = 'Connexion…';
  try {
    await signInWithEmailAndPassword(auth, emailInput.value.trim(), passwordInput.value);
  } catch (error) {
    setAuthError(authMessage(error));
  } finally {
    submitButton.disabled = false;
    submitButton.textContent = 'Se connecter';
  }
});

document.getElementById('authTogglePw')?.addEventListener('click', event => {
  const button = event.currentTarget;
  const showing = passwordInput.type === 'text';
  passwordInput.type = showing ? 'password' : 'text';
  button.setAttribute('aria-pressed', String(!showing));
  button.setAttribute('aria-label', showing ? 'Afficher le mot de passe' : 'Masquer le mot de passe');
});

document.getElementById('authForgotPassword')?.addEventListener('click', async event => {
  event.preventDefault();
  const email = emailInput.value.trim();
  if (!email) {
    setAuthError('Saisissez votre adresse e-mail ci-dessus, puis cliquez à nouveau sur « Mot de passe oublié ».');
    emailInput.focus();
    return;
  }
  try {
    await resetPassword(email);
  } finally {
    // Always the same neutral message, whether or not the address exists —
    // this is what prevents account enumeration via this flow.
    setAuthError('Si un compte existe pour cette adresse, un e-mail de réinitialisation vient d’être envoyé.');
  }
});

document.getElementById('firebaseSignOut')?.addEventListener('click', async () => {
  await signOut(auth);
  clearLocalMirror();
});

onAuthStateChanged(auth, async user => {
  unsubscribers.forEach(unsubscribe => unsubscribe());
  unsubscribers = [];
  started = false;
  window.ML_FIREBASE.ready = false;
  window.ML_FIREBASE.isAdmin = false;
  window.ML_FIREBASE.role = null;
  if (!user) {
    showLogin();
    clearLocalMirror();
    return;
  }

  try {
    setBadge('Chargement…');
    const member = await getDoc(doc(membersRef, user.uid));
    // No self-provisioning: a membership document must already exist, created
    // by an existing admin/owner. Authentication alone grants nothing.
    if (!member.exists()) {
      await signOut(auth);
      clearLocalMirror();
      showLogin('Ce compte n’a pas encore d’accès à Maison Liik. Demandez à un administrateur de vous ajouter.');
      return;
    }
    if (member.data().active !== true) {
      await signOut(auth);
      clearLocalMirror();
      showLogin('Cet accès a été désactivé. Contactez un administrateur.');
      return;
    }
    const role = member.data().role;
    if (!Object.values(ROLES).includes(role)) {
      await signOut(auth);
      clearLocalMirror();
      showLogin('Ce compte a un rôle non reconnu. Contactez un administrateur.');
      return;
    }
    window.ML_FIREBASE.role = role;
    // UI-only convenience flag kept for the transition; prefer hasPermission().
    window.ML_FIREBASE.isAdmin = role === ROLES.OWNER || role === ROLES.ADMIN;
    const state = await readState();
    remoteOrders = state.orders;
    remoteProducts = state.products;
    remoteSettings = state.settings;
    const needsDataMigration = !Array.isArray(state.settings?.walletAccounts)
      || !Array.isArray(state.settings?.walletTransactions)
      || state.orders.some(order => !order.paymentMethod || !order.paymentStatus)
      || state.orders.some(order => (order.items || []).some(item => item.product && !item.productId))
      || (!!state.settings?.ads && Object.keys(state.settings.ads).length > 0 && !state.settings.legacyAdsMigrated);
    if (window.ML_CRM) window.ML_CRM.importState(state);
    started = true;
    window.ML_FIREBASE.ready = true;
    showApp(user);
    setBadge(isLocal ? 'Firebase local' : 'Synchronisé', 'ok');
    startListeners();
    if (needsDataMigration && hasPermission(PERMISSIONS.MANAGE_SETTINGS) && window.ML_CRM) {
      await syncState(window.ML_CRM.exportState());
    }
  } catch (error) {
    console.error('Firebase bootstrap failed', error);
    showLogin(authMessage(error));
    clearLocalMirror();
  }
});
